import {
  PdfModalsState,
  PdfUpgradeBrandWrapper,
} from '@ltvco/refresh-lib/payments';
import { Box, styled } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useAccount } from '@ltvco/refresh-lib/v1';
import { useBannerLogic } from '@ltvco/refresh-lib/v2';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useFirstRecordSearch } from 'routes/utils/useFirstRecordSearch';

const AppWrapper = styled('div')<{
  isOnReportView?: boolean;
  isOnChatAIView?: boolean;
  isOnDashboardHomePage?: boolean;
  isDesktop?: boolean;
  isMobile?: boolean;
  isOnMemberPerksView?: boolean;
}>(({
  theme,
  isOnReportView,
  isOnChatAIView,
  isOnDashboardHomePage,
  isDesktop,
  isMobile,
  isOnMemberPerksView,
}) => {
  const hasNoHorizontalMargin =
    isOnReportView || isOnDashboardHomePage || isOnMemberPerksView;
  const horizontalMargin = isOnChatAIView
    ? '8px'
    : hasNoHorizontalMargin
      ? '0'
      : '16px';

  let marginTop: string;
  if (
    isDesktop ||
    isOnChatAIView ||
    isOnReportView ||
    isOnDashboardHomePage ||
    isOnMemberPerksView
  ) {
    marginTop = '0';
  } else {
    marginTop = '16px';
  }

  return {
    marginTop,
    marginBottom: isDesktop ? '0' : '20px',
    marginLeft: horizontalMargin,
    marginRight: horizontalMargin,
    paddingBottom: isOnChatAIView ? '0' : theme.spacing(0.25),
    height: isOnChatAIView ? '100vh' : 'unset',
    backgroundColor:
      isOnChatAIView && isMobile
        ? theme.palette.background.paper
        : theme.palette.background.default,
    [theme.breakpoints.up('md')]: {
      margin: 0,
    },
  };
});

export function AppContainer({
  children,
}: {
  children: React.ReactNode | React.ReactNode[];
}) {
  const location = useLocation();
  const { isDesktop, isMobile } = useScreenSize();
  const { isUserInTest } = useBannerLogic();
  const { data: account } = useAccount(true);
  const isOnDashboardHomePage = location.pathname.includes('/dashboard/home');
  const isOnReportView = location.pathname.includes('/report/');
  const isOnChatAIView = location.pathname.includes('/bumper-chat');
  const isOnMemberPerksView = location.pathname.includes('/member-perks');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { showFirstTimePDFModal } = useFirstRecordSearch();

  // START STATE_MANAGEMENT
  const [pdfModalState, setPdfModalState] = useState<PdfModalsState>(
    PdfModalsState.None,
  );
  // END STATE_MANAGEMENT

  // BannerMigrationCta imperatively writes marginTop to #app-container via
  // getElementById. When the banner is not active, it still writes a non-zero
  // fallback (theme.spacing(7) = 56px) which overrides the styled component.
  // Clear the inline style whenever the banner is not legitimately showing so
  // the styled component's margin rules always win.
  const isMigrationBannerActive =
    isUserInTest && isOnDashboardHomePage && isDesktop;
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el || isMigrationBannerActive) return;

    // BannerMigrationCta writes marginTop imperatively via getElementById.
    // Use a MutationObserver to immediately revert any inline marginTop write
    // when the migration banner is not active.
    const observer = new MutationObserver(() => {
      if (el.style.marginTop) {
        el.style.marginTop = '';
      }
    });
    observer.observe(el, { attributes: true, attributeFilter: ['style'] });
    // Clear any value already written before this effect ran.
    el.style.marginTop = '';

    return () => observer.disconnect();
  }, [isMigrationBannerActive]);

  useEffect(() => {
    if (showFirstTimePDFModal) {
      setPdfModalState(PdfModalsState.AddonCheckoutModal);
    }
  }, [showFirstTimePDFModal]);

  useEffect(() => {
    if (account?.account?.user_info?.password_delayed) {
      window.open('https://www.bumper.com/account/password_update', '_self');
    }
  }, [account?.account?.user_info?.password_delayed]);

  return (
    <AppWrapper
      ref={wrapperRef}
      id="app-container"
      isOnReportView={isOnReportView}
      isOnChatAIView={isOnChatAIView}
      isDesktop={isDesktop}
      isMobile={isMobile}
      isOnDashboardHomePage={isOnDashboardHomePage}
      isOnMemberPerksView={isOnMemberPerksView}
    >
      <Box>{children}</Box>
      <PdfUpgradeBrandWrapper
        modalState={pdfModalState}
        setModalState={setPdfModalState}
      />
    </AppWrapper>
  );
}
